/* eslint-disable @typescript-eslint/no-explicit-any */
import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState, ReactElement } from "react";
import { motion } from "framer-motion";
import CoursesIcon from "../Icons/CoursesIcon";
import StudentsIcon from "../Icons/StudentsIcon";
import StudentIcon from "../Icons/StudentIcon";
import Logo from "../../assets/NWLogo.png";
import clsx from "clsx";
import React from "react";
import QuestionMarkIcon from "../Icons/QuestionMarkIcon";
import TeamIcon from "../Icons/TeamIcon";
import MoneyIcon from "../Icons/MoneyIcon";
import ReviewIcon from "../Icons/ReviewIcon";
import FeedbackIcon from "../Icons/FeedbackIcon";
import AnnouncementIcon from "../Icons/AnnouncementIcon";
import CouponIcon from "../Icons/CouponIcon";
import PackageIcon from "../Icons/PackageIcon";
import BlogIcon from "../Icons/BlogIcon";
import {
  Handshake,
  HeadphonesIcon,
  ListCollapse,
  Mail,
  PanelLeftClose,
  PanelLeftOpen,
  SquarePercent,
  UserCog2,
  DollarSign,
  Map,
} from "lucide-react";
import { BookOpenText } from "lucide-react";
import HostQuizIcon from "../Icons/HostQuizIcon";
import BankIcon from "../Icons/BankIcon";
import { useUserDataStore } from "../../stores/user";
import GenerateExamIcon from "../Icons/GenerateExamIcon";
interface ISidebarLinks {
  label: string;
  children: { title: string; icon: ReactElement; roles: string[] }[];
}

const Sidebar = ({
  mobileOpen = false,
  onMobileClose,
}: {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}) => {
  const location = useLocation();
  const { userData } = useUserDataStore();
  const userRole = userData?.role || "";
  const [isMobile, setIsMobile] = useState(false);
  const sidebarLinks: ISidebarLinks[] = [
    {
      label: "Learning",
      children: [
        {
          title: "Students",
          icon: <StudentsIcon />,
          roles: ["admin", "sales"],
        },
        {
          title: "Roadmaps",
          icon: <Map />,
          roles: ["admin"],
        },
        {
          title: "Courses",
          icon: <CoursesIcon />,
          roles: ["admin", "sales"],
        },
        {
          title: userRole === "admin" ? "Books" : "Books Requests",
          icon: <BookOpenText />,
          roles: ["admin", "sales"],
        },
        {
          title: "Categories",
          icon: <ListCollapse />,
          roles: ["admin"],
        },
        {
          title: "Packages",
          icon: <PackageIcon />,
          roles: ["admin", "sales"],
        },
        {
          title: "Assign Course",
          icon: <StudentIcon />,
          roles: ["admin", "sales"],
        },
      ],
    },
    {
      label: "Operations",
      children: [
        {
          title: "Announcement",
          icon: <AnnouncementIcon />,
          roles: ["admin"],
        },
        {
          title: "Coupons",
          icon: <CouponIcon />,
          roles: ["admin", "sales"],
        },
        {
          title: "Requests",
          icon: <MoneyIcon />,
          roles: ["admin", "sales"],
        },
        {
          title: "Instructors",
          icon: <TeamIcon />,
          roles: ["admin"],
        },
        {
          title: "Consultants",
          icon: <Handshake />,
          roles: ["admin", "sales"],
        },
        {
          title: "Training",
          icon: <Handshake />,
          roles: ["admin", "sales"],
        },
        {
          title: "Team",
          icon: <TeamIcon />,
          roles: ["admin"],
        },
        {
          title: "Support",
          icon: <HeadphonesIcon />,
          roles: ["admin", "sales"],
        },
      ],
    },
    {
      label: "Content",
      children: [
        {
          title: "Reviews",
          icon: <ReviewIcon />,
          roles: ["admin", "sales"],
        },
        {
          title: "FAQs",
          icon: <QuestionMarkIcon />,
          roles: ["admin"],
        },
        {
          title: "Feedback",
          icon: <FeedbackIcon />,
          roles: ["admin"],
        },
        {
          title: "Blogs",
          icon: <BlogIcon />,
          roles: ["admin"],
        },
        {
          title: "Host Quizs",
          icon: <HostQuizIcon />,
          roles: ["admin"],
        },
        {
          title: "Question Bank",
          icon: <BankIcon />,
          roles: ["admin"],
        },
      ],
    },
    {
      label: "Business",
      children: [
        {
          title: "Sales",
          icon: <SquarePercent />,
          roles: ["admin"],
        },
        {
          title: "Mails",
          icon: <Mail />,
          roles: ["admin"],
        },
        {
          title: "Exam Maker",
          icon: <GenerateExamIcon />,
          roles: ["admin"],
        },
        {
          title: "Admins",
          icon: <UserCog2 />,
          roles: ["admin"],
        },
        {
          title: "Transactions",
          icon: <MoneyIcon />,
          roles: ["admin", "sales"],
        },
        {
          title: "Currency",
          icon: <DollarSign />,
          roles: ["admin"],
        },
      ],
    },
  ];

  const [isOpen, setIsOpen] = useState<boolean>(true);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1023px)");
    const handleChange = () => setIsMobile(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);

    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const sidebarWidth = isMobile ? 284 : isOpen ? 284 : 88;

  return (
    <>
      {mobileOpen && (
        <button
          type="button"
          aria-label="Close dashboard navigation"
          onClick={onMobileClose}
          className="fixed inset-0 z-[130] bg-primary/35 backdrop-blur-sm lg:hidden"
        />
      )}
      <motion.aside
        animate={{
          width: sidebarWidth,
          x: isMobile && !mobileOpen ? -304 : 0,
        }}
        transition={{ duration: 0.22, ease: "easeOut" }}
        className="admin-sidebar fixed inset-y-0 left-0 z-[140] flex h-dvh shrink-0 flex-col border-r border-white/10 bg-primary text-white shadow-[16px_0_40px_rgba(12,48,82,0.18)] lg:sticky lg:top-0 lg:z-50 lg:h-screen"
      >
      <div className="flex h-[76px] items-center justify-between gap-3 border-b border-white/10 px-4">
        <Link
          className={clsx(
            "flex min-w-0 items-center gap-3",
            !isOpen && "mx-auto justify-center"
          )}
          to="/"
        >
          <span className="grid size-11 shrink-0 place-items-center rounded-2xl bg-white shadow-sm">
            <img src={Logo} className="size-8 object-contain" alt="NileWise" />
          </span>
          {isOpen && (
            <span className="min-w-0">
              <span className="block text-base font-bold leading-tight">
                NileWise
              </span>
              <span className="block truncate text-xs font-medium text-white/50">
                Admin workspace
              </span>
            </span>
          )}
        </Link>
        {isOpen && (
          <button
            type="button"
            onClick={() => {
              if (isMobile) {
                onMobileClose?.();
              } else {
                setIsOpen(false);
              }
            }}
            className="grid size-10 place-items-center rounded-xl border border-white/10 text-white/70 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
            aria-label={isMobile ? "Close sidebar" : "Collapse sidebar"}
          >
            <PanelLeftClose size={18} />
          </button>
        )}
      </div>

      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="mx-auto mt-4 hidden size-10 place-items-center rounded-xl border border-white/10 text-white/70 transition hover:border-white/20 hover:bg-white/10 hover:text-white lg:grid"
          aria-label="Expand sidebar"
        >
          <PanelLeftOpen size={18} />
        </button>
      )}

      <nav className="admin-sidebar-nav flex-1 space-y-6 overflow-y-auto px-3 py-5">
        {sidebarLinks.map((section) => {
          const visibleLinks = section.children.filter(
            (child) => !child.roles || child.roles.includes(userRole)
          );

          if (!visibleLinks.length) return null;

          return (
            <div key={section.label} className="space-y-2">
              {isOpen && (
                <p className="px-3 text-[11px] font-bold uppercase text-white/35">
                  {section.label}
                </p>
              )}
              <div className="space-y-1.5">
                {visibleLinks.map((child) => {
                  const path = child.title.toLowerCase().split(" ").join("-");
                  const isPathActive = location.pathname.includes(path);

                  return (
                    <NavLink
                      key={child.title}
                      to={path}
                      onClick={() => {
                        if (isMobile) onMobileClose?.();
                      }}
                      className={({ isActive }) =>
                        clsx(
                          "group relative flex h-11 items-center gap-3 rounded-xl px-3 text-sm font-semibold text-white/70 transition duration-200",
                          isOpen ? "justify-start" : "justify-center",
                          (isActive || isPathActive) &&
                            "bg-white text-primary shadow-[0_10px_28px_rgba(254,87,34,0.18)]",
                          !(isActive || isPathActive) &&
                            "hover:bg-white/10 hover:text-white"
                        )
                      }
                    >
                      {({ isActive }) => {
                        const selected = isActive || isPathActive;

                        return (
                          <>
                            {selected && (
                              <span className="absolute left-0 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-secondary" />
                            )}
                            <span
                              className={clsx(
                                "admin-sidebar-icon grid size-8 shrink-0 place-items-center rounded-lg transition",
                                selected
                                  ? "bg-secondary/10 text-secondary"
                                  : "bg-white/5 text-white/60 group-hover:bg-white/10 group-hover:text-white"
                              )}
                            >
                              {React.cloneElement(child.icon, {
                                isSelected: selected,
                              })}
                            </span>
                            {isOpen ? (
                              <span className="truncate">{child.title}</span>
                            ) : (
                              <span className="pointer-events-none absolute left-full ml-3 rounded-lg border border-white/10 bg-primary px-3 py-2 text-xs font-semibold text-white opacity-0 shadow-xl transition group-hover:opacity-100">
                                {child.title}
                              </span>
                            )}
                          </>
                        );
                      }}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>
      </motion.aside>
    </>
  );
};

export default Sidebar;
